import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Form, Card, Button, Breadcrumb, Table, Pagination } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    createHolidayEntry,
    getAllHolidayEntries,
    updateHolidayEntry,
    getHolidayTypes,
} from "../../../../api/onboarddeboard";

const ITEMS_PER_PAGE = 20;

const HolidayEntry = () => {
    const token = localStorage.getItem("token");

    // ── Form state ──────────────────────────────────────────
    const [holidayName, setHolidayName] = useState("");
    const [date, setDate] = useState("");
    const [description, setDescription] = useState("");
    const [holidayTypeId, setHolidayTypeId] = useState("");
    const [errors, setErrors] = useState({});

    // ── Edit mode ───────────────────────────────────────────
    const [editingId, setEditingId] = useState(null);

    // ── Dropdown data ───────────────────────────────────────
    const [holidayTypes, setHolidayTypes] = useState([]);

    // ── Table / list state ──────────────────────────────────
    const [entries, setEntries] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [sortOption, setSortOption] = useState("latest");

    // ── Fetch dropdown + list ────────────────────────────────
    const fetchHolidayTypes = useCallback(async () => {
        try {
            const res = await getHolidayTypes(token);
            if (res.status === 200) {
                const activeTypes = (res.data || []).filter((t) => t.status === true);
                setHolidayTypes(activeTypes);
            }
        } catch (err) {
            toast.error("Failed to load holiday types.");
        }
    }, [token]);

    const fetchEntries = useCallback(async () => {
        try {
            const res = await getAllHolidayEntries(token);
            const sorted = (res.data || []).sort((a, b) => b.id - a.id);
            setEntries(sorted);
        } catch (err) {
            toast.error("Failed to fetch holiday entries.");
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            fetchHolidayTypes();
            fetchEntries();
        }
    }, [token, fetchHolidayTypes, fetchEntries]);

    // ── Validation ──────────────────────────────────────────
    const validate = () => {
        const newErrors = {};
        const trimmedValue = holidayName.trim();

        if (/^\s+$/.test(holidayName)) {
            newErrors.holidayName = "Holiday Name cannot be only spaces.";
        } else if (!trimmedValue) {
            newErrors.holidayName = "Holiday Name is required";
        } else if (/^\d+$/.test(trimmedValue)) {
            newErrors.holidayName = "Holiday Name cannot be only numeric.";
        } else if (/^[^a-zA-Z0-9]+$/.test(trimmedValue)) {
            newErrors.holidayName = "Holiday Name cannot be only special characters.";
        } else if (
            /^[^a-zA-Z]*$/.test(trimmedValue) &&
            /[0-9]/.test(trimmedValue) &&
            /[^a-zA-Z0-9]/.test(trimmedValue)
        ) {
            newErrors.holidayName = "Holiday Name cannot be only numbers and special characters.";
        } else if (trimmedValue.length > 100) {
            newErrors.holidayName = "Holiday Name must not exceed 100 characters";
        }
if (!date) newErrors.date = "Date is required";
if (!holidayTypeId) newErrors.holidayTypeId = "Holiday Type is required";
if (description.length > 500) newErrors.description = "Description must not exceed 500 characters";
if (description.trim() && !/[a-zA-Z]/.test(description.trim())) newErrors.description = "Description must contain at least one letter.";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleHolidayNameChange = (e) => {
        const value = e.target.value;
        setHolidayName(value);
        const trimmedValue = value.trim();

        if (/^\s+$/.test(value)) {
            setErrors((prev) => ({ ...prev, holidayName: "Holiday Name cannot be only spaces." }));
            return;
        }
        if (/^\d+$/.test(trimmedValue)) {
            setErrors((prev) => ({ ...prev, holidayName: "Holiday Name cannot be only numeric." }));
            return;
        }
        if (/^[^a-zA-Z0-9]+$/.test(trimmedValue)) {
            setErrors((prev) => ({ ...prev, holidayName: "Holiday Name cannot be only special characters." }));
            return;
        }
        if (
            /^[^a-zA-Z]*$/.test(trimmedValue) &&
            /[0-9]/.test(trimmedValue) &&
            /[^a-zA-Z0-9]/.test(trimmedValue)
        ) {
            setErrors((prev) => ({ ...prev, holidayName: "Holiday Name cannot be only numbers and special characters." }));
            return;
        }
        if (trimmedValue.length > 100) {
            setErrors((prev) => ({ ...prev, holidayName: "Holiday Name cannot exceed 100 characters." }));
            return;
        }
        setErrors((prev) => ({ ...prev, holidayName: "" }));
    };

    const handleDescriptionChange = (e) => {
        const value = e.target.value;
        if (value.length > 500) {
            setErrors((prev) => ({ ...prev, description: "Description cannot exceed 500 characters." }));
            return;
        }
        setDescription(value);
        if (value.trim() && !/[a-zA-Z]/.test(value)) {
            setErrors((prev) => ({ ...prev, description: "Description must contain at least one letter." }));
            return;
        }
        setErrors((prev) => ({ ...prev, description: "" }));
    };


    // ── Submit (create or update) ────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const payload = {
            holidayName: holidayName.trim(),
            date,
            description: description.trim(),
        };

        try {
            if (editingId) {
                await updateHolidayEntry(editingId, holidayTypeId, payload, token);
                toast.success("Holiday entry updated successfully");
            } else {
                await createHolidayEntry(holidayTypeId, payload, token);
                toast.success("Holiday entry created successfully");
            }
            await fetchEntries();
            resetForm();
        } catch (error) {
            const data = error?.response?.data;
            let message =
                typeof data === "string"
                    ? data
                    : data?.message || data?.error || data?.errorMessage || data?.detail;


            if (!message || !message.trim()) {
                message = "A holiday entry for this date already exists under this holiday type";
            }

            toast.error(message);
        }
    };

    const handleEdit = (entry) => {
        setEditingId(entry.id);
        setHolidayName(entry.holidayName);
        setDate(entry.date);
        setDescription(entry.description || "");
        setHolidayTypeId(String(entry.holidayType?.id || ""));
        setErrors({});
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const resetForm = () => {
        setHolidayName("");
        setDate("");
        setDescription("");
        setHolidayTypeId("");
        setEditingId(null);
        setErrors({});
    };

    const handleCancel = () => resetForm();

    // ── Filter / sort / paginate ─────────────────────────────
    const displayed = entries
        .filter((entry) => {
            const matchesSearch = (entry.holidayName || "")
                .toLowerCase()
                .includes(searchTerm.toLowerCase().trim());
            const matchesType = typeFilter === "" || String(entry.holidayType?.id) === typeFilter;
            return matchesSearch && matchesType;
        })
        .sort((a, b) => {
            if (sortOption === "dateAsc") return new Date(a.date) - new Date(b.date);
            if (sortOption === "nameAZ") return a.holidayName.localeCompare(b.holidayName);
            return b.id - a.id;
        });

    const totalPages = Math.ceil(displayed.length / ITEMS_PER_PAGE);
    const paginated = displayed.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const exportToCSV = () => {
        if (entries.length === 0) {
            toast.error("No data available to export");
            return;
        }
        const headers = ["#", "Holiday Name", "Date", "Holiday Type", "Description"];
        const rows = entries.map((entry, index) => [
            index + 1,
            entry.holidayName,
            entry.date,
            entry.holidayType?.typeName || "-",
            entry.description || "",
        ]);
        const csvContent = [headers, ...rows].map((row) => row.map((c) => `"${c}"`).join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "holiday_entries.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <Container fluid className="p-6">
            <style>{`
                .lc-breadcrumb a {
                    text-decoration: none !important;
                    color: #2E5CB8 !important;
                }
                .lc-breadcrumb .breadcrumb-item.active {
                    color: #6c757d !important;
                }
                .pagination .page-item.active .page-link {
                    background-color: #2E5CB8 !important;
                    border-color: #2E5CB8 !important;
                }
            `}</style>
 <h1 className="page-main-heading" style={{ marginBottom: 12 }}>
                Holiday Entry
            </h1>
            <ToastContainer position="top-right" autoClose={3000} />
            <Breadcrumb className="lc-breadcrumb">
                <Breadcrumb.Item href="#">Leave</Breadcrumb.Item>
                <Breadcrumb.Item href="#">Master</Breadcrumb.Item>
                <Breadcrumb.Item active>Holiday Entry</Breadcrumb.Item>
            </Breadcrumb>

            {/* ── Form Card ── */}
            <Row className="mb-4">
                <Col xl={12}>
                    <Card>
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                Holiday Name<span className="text-danger">*</span>
                                            </Form.Label>
                                            <Col sm={8}>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter Holiday Name"
                                                    value={holidayName}
                                                    maxLength={100}
                                                    onChange={handleHolidayNameChange}
                                                />
                                                {errors.holidayName && <p style={{ color: "red", fontSize: "13px" }}>{errors.holidayName}</p>}
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                Date<span className="text-danger">*</span>
                                            </Form.Label>
                                            <Col sm={8}>
                                                <Form.Control
                                                    type="date"
                                                    value={date}
                                                    onClick={(e) => {
                                                        if (e.target.showPicker) {
                                                            e.target.showPicker();
                                                        }
                                                    }}
                                                    onChange={(e) => {
                                                        setDate(e.target.value);
                                                        setErrors((prev) => ({ ...prev, date: "" }));
                                                    }}
                                                />
                                                {errors.date && <p style={{ color: "red", fontSize: "13px" }}>{errors.date}</p>}
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                Holiday Type<span className="text-danger">*</span>
                                            </Form.Label>
                                            <Col sm={8}>
                                                <Form.Select
                                                    value={holidayTypeId}
                                                    onChange={(e) => {
                                                        setHolidayTypeId(e.target.value);
                                                        setErrors((prev) => ({ ...prev, holidayTypeId: "" }));
                                                    }}
                                                >
                                                    <option value="">Select Holiday Type</option>
                                                    {holidayTypes.map((t) => (
                                                        <option key={t.id} value={t.id}>{t.typeName}</option>
                                                    ))}
                                                </Form.Select>
                                                {errors.holidayTypeId && <p style={{ color: "red", fontSize: "13px" }}>{errors.holidayTypeId}</p>}
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                Description
                                            </Form.Label>
                                            <Col sm={8}>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={1}
                                                    placeholder="Enter Description  "
                                                    value={description}
                                                    onChange={handleDescriptionChange}
                                                />
                                                {errors.description && <p style={{ color: "red", fontSize: "13px" }}>{errors.description}</p>}
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>

                                <Row className="align-items-center">
                                    <Col className="mt-2 d-flex align-items-center justify-content-center">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            style={{ backgroundColor: "#2E5CB8", borderColor: "#2E5CB8" }}
                                        >
                                            {editingId ? "Update" : "Submit"}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            type="button"
                                            style={{ marginLeft: "10px" }}
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ── List Card ── */}
            <Row className="mb-8">
                <Col xl={12}>
                    <Card>
                        <Card.Body>
                            <Row className="mb-3 d-flex justify-content-end align-items-center g-2">
                                <Col md={3}>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by Holiday Name..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Select
                                        value={typeFilter}
                                        onChange={(e) => {
                                            setTypeFilter(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="">All Types</option>
                                        {holidayTypes.map((t) => (
                                            <option key={t.id} value={t.id}>{t.typeName}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Select
                                        value={sortOption}
                                        onChange={(e) => {
                                            setSortOption(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="latest">Latest First</option>
                                        <option value="dateAsc">Date (Ascending)</option>
                                        <option value="nameAZ">Name A→Z</option>
                                    </Form.Select>
                                </Col>
                                <Col md="auto">
                                    <i
                                        className="bi bi-download"
                                        title="Export CSV"
                                        style={{ fontSize: "1.3rem", cursor: "pointer", color: "#2E5CB8" }}
                                        onClick={exportToCSV}
                                    />
                                </Col>
                            </Row>

                            <Table bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Sr.No</th>
                                        <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Holiday Name</th>
                                        <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Date</th>
                                        <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Holiday Type</th>
                                        <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Description</th>
                                        <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.length > 0 ? (
                                        paginated.map((entry, index) => (
                                            <tr key={entry.id}>
                                                <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                                                <td>{entry.holidayName}</td>
                                                <td>{entry.date}</td>
                                                <td>{entry.holidayType?.typeName || "-"}</td>
                                                <td>{entry.description || "-"}</td>
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-secondary"
                                                        onClick={() => handleEdit(entry)}
                                                        type="button"
                                                        style={{ padding: "5px 10px" }}
                                                    >
                                                        <i className="bi bi-pencil-fill me-2" />Edit
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={6} style={{ textAlign: "center" }}>No holiday entries found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>

                            <div className="d-flex justify-content-end">
                                <Pagination>
                                    <Pagination.Prev
                                        onClick={() => setCurrentPage((p) => p - 1)}
                                        disabled={currentPage === 1}
                                    />
                                    {[...Array(totalPages).keys()].map((n) => (
                                        <Pagination.Item
                                            key={n + 1}
                                            active={n + 1 === currentPage}
                                            onClick={() => setCurrentPage(n + 1)}
                                        >
                                            {n + 1}
                                        </Pagination.Item>
                                    ))}
                                    <Pagination.Next
                                        onClick={() => setCurrentPage((p) => p + 1)}
                                        disabled={currentPage === totalPages || totalPages === 0}
                                    />
                                </Pagination>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default HolidayEntry;
